import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  TrendingUp as SalesIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import client from '../../api/client';
import CustomTextField from '../../components/CustomTextField';
import { toPersianDigits } from '../../utils/numberConverter';

const Salespersons = () => {
  const [salespersons, setSalespersons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentSalesperson, setCurrentSalesperson] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    mobile: '',
  });
  const [formLoading, setFormLoading] = useState(false);

  useEffect(() => {
    fetchSalespersons();
  }, []);

  const fetchSalespersons = async () => {
    setLoading(true);
    try {
      const response = await client.get('/users/salespersons/');
      setSalespersons(response.data || []);
    } catch (error) {
      toast.error('خطا در دریافت لیست فروشندگان');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (salesperson = null) => {
    if (salesperson) {
      setEditMode(true);
      setCurrentSalesperson(salesperson);
      setFormData({
        name: salesperson.name || '',
        mobile: salesperson.mobile || '',
      });
    } else {
      setEditMode(false);
      setCurrentSalesperson(null);
      setFormData({
        name: '',
        mobile: '',
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setFormData({ name: '', mobile: '' });
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.mobile) {
      toast.error('نام و موبایل الزامی است');
      return;
    }

    setFormLoading(true);
    try {
      if (editMode) {
        await client.patch(`/users/salespersons/${currentSalesperson.id}/`, formData);
        toast.success('فروشنده ویرایش شد');
      } else {
        await client.post('/users/salespersons/', formData);
        toast.success('فروشنده ایجاد شد - رمز عبور همان شماره موبایل است');
      }
      handleCloseDialog();
      fetchSalespersons();
    } catch (error) {
      const errorData = error.response?.data;
      let message = 'خطا در ذخیره فروشنده';
      if (errorData?.mobile) {
        message = Array.isArray(errorData.mobile) ? errorData.mobile[0] : errorData.mobile;
      } else if (errorData?.name) {
        message = Array.isArray(errorData.name) ? errorData.name[0] : errorData.name;
      } else if (errorData?.error) {
        message = errorData.error;
      }
      toast.error(message);
    } finally {
      setFormLoading(false);
    }
  };

  const handleToggleActive = async (salesperson) => {
    try {
      await client.patch(`/users/salespersons/${salesperson.id}/`, {
        is_active: !salesperson.is_active,
      });
      toast.success(salesperson.is_active ? 'فروشنده غیرفعال شد' : 'فروشنده فعال شد');
      fetchSalespersons();
    } catch (error) {
      toast.error('خطا در تغییر وضعیت');
    }
  };

  const formatNumber = (num) => {
    if (!num) return '۰';
    return toPersianDigits(num.toLocaleString('fa-IR'));
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* هدر */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#1a237e' }}>
          مدیریت فروشندگان
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          sx={{
            background: 'linear-gradient(45deg, #1a237e 30%, #3949ab 90%)',
            '&:hover': {
              background: 'linear-gradient(45deg, #0d1642 30%, #1a237e 90%)',
            },
          }}
        >
          فروشنده جدید
        </Button>
      </Box>

      {/* لیست فروشندگان */}
      {salespersons.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 5 }}>
            <PersonIcon sx={{ fontSize: 60, color: '#ccc', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              هنوز فروشنده‌ای ثبت نشده است
            </Typography>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
              sx={{ mt: 2 }}
            >
              افزودن اولین فروشنده
            </Button>
          </CardContent>
        </Card>
      ) : (
        <TableContainer component={Paper} elevation={3}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>نام فروشنده</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>موبایل</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>وضعیت</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>تعداد فروش</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>مبلغ فروش (ماه جاری)</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }} align="center">عملیات</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {salespersons.map((salesperson) => (
                <TableRow key={salesperson.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PersonIcon sx={{ color: '#1a237e' }} />
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        {salesperson.name}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PhoneIcon sx={{ fontSize: 18, color: '#666' }} />
                      <Typography variant="body2" sx={{ direction: 'ltr', textAlign: 'right' }}>
                        {toPersianDigits(salesperson.mobile)}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={salesperson.is_active ? 'فعال' : 'غیرفعال'}
                      color={salesperson.is_active ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <SalesIcon sx={{ fontSize: 18, color: '#1a237e' }} />
                      <Typography variant="body2">
                        {formatNumber(salesperson.total_sales_count || 0)} سفارش
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#2e7d32' }}>
                      {formatNumber(salesperson.monthly_sales_amount || 0)} ریال
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      onClick={() => handleOpenDialog(salesperson)}
                      sx={{ color: '#1a237e' }}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleToggleActive(salesperson)}
                      sx={{ color: salesperson.is_active ? '#f44336' : '#4caf50' }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* دیالوگ افزودن/ویرایش */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              {editMode ? 'ویرایش فروشنده' : 'فروشنده جدید'}
            </Typography>
            <IconButton onClick={handleCloseDialog} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {!editMode && (
              <Alert severity="info" sx={{ mb: 1 }}>
                رمز عبور پیش‌فرض همان شماره موبایل است. فروشنده در اولین ورود باید آن را تغییر دهد.
              </Alert>
            )}
            
            <CustomTextField
              fullWidth
              label="نام و نام خانوادگی"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonIcon />
                  </InputAdornment>
                ),
              }}
            />

            <CustomTextField
              fullWidth
              label="شماره موبایل"
              value={formData.mobile}
              onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
              required
              inputProps={{ maxLength: 11 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PhoneIcon />
                  </InputAdornment>
                ),
              }}
              helperText="موبایل به عنوان نام کاربری و رمز عبور اولیه استفاده می‌شود"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseDialog} disabled={formLoading}>
            انصراف
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={formLoading}
            sx={{
              background: 'linear-gradient(45deg, #1a237e 30%, #3949ab 90%)',
            }}
          >
            {formLoading ? <CircularProgress size={24} /> : editMode ? 'ذخیره' : 'ایجاد'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Salespersons;